import React from 'react';

import { Button, Progress } from 'antd-v5';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import StatusTag, { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';
import { Text } from 'scouting-ui/lib/Text';
import { cssVars } from 'scouting-ui/lib/variables';

import type { YouthRankAdvancement } from '@appTypes/esb';
import {
  isCompleted,
  isStarted,
  statusColor,
  tagStatus,
  toPercent,
} from '@shared/utils/advancementDisplay';

import AdvancementAuditTooltip, {
  formatAuditDate,
} from '../AdvancementAudit/AdvancementAuditTooltip';
import AdvancementImage from '../AdvancementImage/AdvancementImage';
import styles from './YouthRanksCard.module.scss';

export interface RankRowProps {
  rank: YouthRankAdvancement;
  /** The youth is working on this rank: color it as started even at 0%. */
  isCurrent?: boolean;
  onViewMore?: (rankId: number) => void;
}

export default function RankRow(props: RankRowProps) {
  const { rank, isCurrent, onViewMore } = props;

  const tag = tagStatus(rank.status);

  // A rank the youth has reached but not started shows as started, so the
  // progress bar reads as "up next" rather than as one more untouched rank.
  const barTag =
    isCurrent && !isCompleted(rank.status) ? StatusProp.STARTED : tag;
  // `statusColor` answers in `ColorType`, which is what `Text` wants; the
  // progress bar needs the resolved hex instead
  const color = statusColor(barTag);

  const percent = toPercent(rank.percentCompleted);
  const earnedDate = formatAuditDate(rank.dateEarned);

  return (
    <div className={styles.row}>
      <AdvancementImage src={rank.imageUrl100} className={styles.image} />

      <div className={styles.details}>
        <div className={styles.topBar}>
          <Text strong className={styles.name}>
            {rank.name}
          </Text>

          <div className={styles.meta}>
            {!!earnedDate && (
              <Text
                font="body-caption"
                color="color-text-medium"
                className={styles.earned}
              >
                <FormattedMessage id="youthRanksCard.completed" /> {earnedDate}
              </Text>
            )}

            {isStarted(rank.status) && (
              <AdvancementAuditTooltip advancement={rank}>
                <span className={styles.status}>
                  <StatusTag status={tag} />
                  <MdInfo
                    className={styles.infoIcon}
                    color={cssVars['color-info']}
                  />
                </span>
              </AdvancementAuditTooltip>
            )}

            <Text strong color={color}>
              {`${percent}%`}
            </Text>
          </div>
        </div>

        <div className={styles.progress}>
          <Progress
            percent={percent}
            strokeColor={cssVars[color]}
            strokeLinecap="round"
            showInfo={false}
            size={['100%', 10]}
          />
          {onViewMore && (
            <Button
              size="small"
              type="default"
              className={styles.viewMore}
              onClick={() => onViewMore(rank.id)}
            >
              <FormattedMessage id="youthRanksCard.viewMore" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
